import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "../../providers/NotificationProvider";
import DraggablePaper from "../../components/common/DraggablePaper";
import {
  fetchWorkerDetail,
  addWorkerAchievement,
  deleteWorkerAchievement,
} from "../../api/memberApi";

// ── 実績追加ダイアログ ──────────────────────────────────────────────
function AchievementFormDialog({ open, onClose, onSaved, workerId, goals }) {
  const intl = useIntl();
  const notify = useNotification();

  const [selectedGoalIndex, setSelectedGoalIndex] = useState("");
  const [actualValue, setActualValue] = useState("");
  const [actualDate, setActualDate] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedGoalIndex("");
    setActualValue("");
    setActualDate("");
    setErrors({});
    setSubmitting(false);
  }, [open]);

  const validate = () => {
    const e = {};
    if (selectedGoalIndex === "")
      e.goal = intl.formatMessage({ id: "worker.achievement.goal.required" });
    if (!actualValue.trim())
      e.actualValue = intl.formatMessage({ id: "worker.achievement.actualValue.required" });
    if (!actualDate)
      e.actualDate = intl.formatMessage({ id: "worker.achievement.actualDate.required" });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const selectedGoal = goals[selectedGoalIndex];
      await addWorkerAchievement({
        workerId,
        achievement: {
          goal: selectedGoal,
          actualValue: actualValue.trim(),
          actualDate: new Date(actualDate).toISOString(),
          createdAchievementAt: new Date().toISOString(),
        },
      });
      notify.success(intl.formatMessage({ id: "worker.achievement.createSuccess" }));
      onSaved();
    } catch (err) {
      notify.apiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
      <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>
        {intl.formatMessage({ id: "worker.achievement.add.title" })}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        {/* 目標選択 */}
        <FormControl fullWidth error={Boolean(errors.goal)}>
          <InputLabel>{intl.formatMessage({ id: "worker.achievement.goal" })}</InputLabel>
          <Select
            value={selectedGoalIndex}
            label={intl.formatMessage({ id: "worker.achievement.goal" })}
            onChange={(e) => setSelectedGoalIndex(e.target.value)}
          >
            {goals.map((g, idx) => (
              <MenuItem key={idx} value={idx}>
                {g.indicator?.indicatorName || `目標 ${idx + 1}`}
                {g.plannedValue ? `（計画値: ${g.plannedValue}${g.indicator?.unit || ""}）` : ""}
              </MenuItem>
            ))}
          </Select>
          {errors.goal && <FormHelperText>{errors.goal}</FormHelperText>}
        </FormControl>

        {/* 選択中の目標サマリー */}
        {selectedGoalIndex !== "" && goals[selectedGoalIndex] && (
          <Box
            sx={{
              p: 1.5,
              backgroundColor: "grey.50",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {intl.formatMessage({ id: "worker.goal.deadline" })}:{" "}
              {goals[selectedGoalIndex].deadline
                ? new Date(goals[selectedGoalIndex].deadline).toLocaleDateString()
                : "—"}
            </Typography>
          </Box>
        )}

        {/* 実績値 */}
        <TextField
          label={intl.formatMessage({ id: "worker.achievement.actualValue" })}
          value={actualValue}
          onChange={(e) => setActualValue(e.target.value)}
          error={Boolean(errors.actualValue)}
          helperText={errors.actualValue}
          fullWidth
          size="small"
        />

        {/* 実績日 */}
        <TextField
          label={intl.formatMessage({ id: "worker.achievement.actualDate" })}
          type="date"
          value={actualDate}
          onChange={(e) => setActualDate(e.target.value)}
          error={Boolean(errors.actualDate)}
          helperText={errors.actualDate}
          InputLabelProps={{ shrink: true }}
          fullWidth
          size="small"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {intl.formatMessage({ id: "common.cancel" })}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            intl.formatMessage({ id: "common.create" })
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── メインページ ──────────────────────────────────────────────────────
export default function WorkerAchievementListPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { workerId } = useParams();
  const notify = useNotification();

  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);

  // ダイアログ
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadWorker = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWorkerDetail(workerId);
      setWorker(data);
    } catch (err) {
      notify.apiError(err);
    } finally {
      setLoading(false);
    }
  }, [workerId, notify]);

  useEffect(() => {
    loadWorker();
  }, [loadWorker]);

  const achievements = worker?.achievements || [];
  const goals = worker?.goals || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  // 削除実行
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteWorkerAchievement({ workerId, achievement: deleteTarget });
      notify.success(intl.formatMessage({ id: "worker.achievement.deleteSuccess" }));
      setDeleteTarget(null);
      loadWorker();
    } catch (err) {
      notify.apiError(err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      {/* ヘッダー */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/workers/${workerId}`)}
          variant="text"
        >
          {intl.formatMessage({ id: "common.back" })}
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {intl.formatMessage({ id: "worker.achievement.list.title" })}
          </Typography>
          {worker?.workerName && (
            <Typography variant="body2" color="text.secondary">
              {worker.workerName}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
          disabled={goals.length === 0}
        >
          {intl.formatMessage({ id: "worker.achievement.add.button" })}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : achievements.length === 0 ? (
        <Box>
          <Typography color="text.secondary">
            {intl.formatMessage({ id: "worker.achievement.noAchievements" })}
          </Typography>
          {goals.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              {intl.formatMessage({ id: "worker.achievement.noGoals" })}
            </Typography>
          )}
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "worker.goal.indicator" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  {intl.formatMessage({ id: "worker.achievement.plannedValue" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  {intl.formatMessage({ id: "worker.achievement.actualValue" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "worker.goal.unit" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "worker.achievement.actualDate" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center" width={80}>
                  {intl.formatMessage({ id: "common.actions" })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {achievements.map((ach, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {ach.goal?.indicator?.indicatorName || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {ach.goal?.plannedValue || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {ach.actualValue || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {ach.goal?.indicator?.unit || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(ach.actualDate)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={intl.formatMessage({ id: "common.delete" })}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget(ach)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 実績追加ダイアログ */}
      <AchievementFormDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={() => { setAddOpen(false); loadWorker(); }}
        workerId={workerId}
        goals={goals}
      />

      {/* 削除確認ダイアログ */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>
          {intl.formatMessage({ id: "worker.achievement.deleteConfirm.title" })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.formatMessage({ id: "worker.achievement.deleteConfirm.message" })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {intl.formatMessage({ id: "common.cancel" })}
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              intl.formatMessage({ id: "common.delete" })
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
